/**
 * SaveManager - Manages game saves using SQLite database
 * Handles save/load, database schema, and data persistence
 */

import { join, dirname } from 'path';
import { app } from 'electron';
import { mkdirSync, existsSync, writeFileSync, readFileSync } from 'fs';
import { createHash, createCipheriv, createDecipheriv, randomBytes } from 'crypto';

// Use require for native module to avoid bundling issues
// @ts-ignore - require is available with nodeIntegration enabled
const BetterSqlite3 = typeof require !== 'undefined' ? require('better-sqlite3') : null;

// Create Database type
type Database = any; // Use any for now since we're using require

// Database interfaces
export interface SaveData {
  id: number;
  slotNumber: number;
  playerName: string;
  currentNight: number;
  currentLevel: string;
  playerHealth: number;
  playerStamina: number;
  playerMaxHealth: number;
  playerMaxStamina: number;
  flashlightBattery: number;
  playerXP: number;
  playerLevel: number;
  playerPosition: string; // JSON string {x, y}
  lastCheckpoint: string; // JSON string
  playTime: number; // seconds
  timestamp: number; // Unix timestamp
  screenshotPath: string | null; // Path to save screenshot
  encryptedData: string | null; // Encrypted game state
  checksum: string | null; // Data integrity checksum
}

export interface InventoryData {
  id: number;
  saveId: number;
  itemId: string;
  itemType: string;
  quantity: number;
}

export interface CollectibleData {
  id: number;
  saveId: number;
  collectibleId: string;
  collectibleType: 'lore' | 'battery' | 'health' | 'upgrade';
  collected: boolean;
  timestamp: number;
}

export interface AchievementData {
  id: number;
  achievementId: string;
  unlocked: boolean;
  unlockedTimestamp: number | null;
  progress: number; // 0-100
}

export interface SettingsData {
  id: number;
  category: string; // 'graphics', 'audio', 'controls', 'gameplay'
  settingKey: string;
  settingValue: string; // JSON string
}

export interface StatsData {
  id: number;
  saveId: number;
  statKey: string;
  statValue: number;
}

export class SaveManager {
  private db: Database | null = null;
  private dbPath: string;
  private isInitialized: boolean = false;

  // Encryption settings
  private readonly ENCRYPTION_ALGORITHM = 'aes-256-cbc';
  private readonly ENCRYPTION_KEY = this.deriveKey('FNAF_KHANS_SECRET_KEY_2025'); // 32 bytes
  private readonly IV_LENGTH = 16; // For AES

  constructor() {
    // Get app data path (cross-platform)
    const userDataPath = app ? app.getPath('userData') : './data';
    this.dbPath = join(userDataPath, 'save_data.db');
    console.log('SaveManager: Database path:', this.dbPath);
  }

  /**
   * Derive a 32-byte encryption key from a passphrase
   */
  private deriveKey(passphrase: string): Buffer {
    return createHash('sha256').update(passphrase).digest();
  }

  /**
   * Generate SHA-256 checksum for data integrity verification
   */
  private generateChecksum(data: string): string {
    return createHash('sha256').update(data).digest('hex');
  }

  /**
   * Encrypt data using AES-256-CBC
   */
  private encryptData(data: string): string {
    try {
      const iv = randomBytes(this.IV_LENGTH);
      const cipher = createCipheriv(this.ENCRYPTION_ALGORITHM, this.ENCRYPTION_KEY, iv);

      let encrypted = cipher.update(data, 'utf8', 'hex');
      encrypted += cipher.final('hex');

      // Return IV + encrypted data
      return iv.toString('hex') + ':' + encrypted;
    } catch (error) {
      console.error('SaveManager: Encryption failed:', error);
      throw new Error('Failed to encrypt save data');
    }
  }

  /**
   * Decrypt data using AES-256-CBC
   */
  private decryptData(encryptedData: string): string {
    try {
      const parts = encryptedData.split(':');
      if (parts.length !== 2) {
        throw new Error('Invalid encrypted data format');
      }

      const iv = Buffer.from(parts[0], 'hex');
      const encrypted = parts[1];

      const decipher = createDecipheriv(this.ENCRYPTION_ALGORITHM, this.ENCRYPTION_KEY, iv);

      let decrypted = decipher.update(encrypted, 'hex', 'utf8');
      decrypted += decipher.final('utf8');

      return decrypted;
    } catch (error) {
      console.error('SaveManager: Decryption failed:', error);
      throw new Error('Failed to decrypt save data - file may be corrupted or tampered');
    }
  }

  /**
   * Validate save data integrity using checksum
   */
  private validateSaveIntegrity(data: string, checksum: string): boolean {
    const calculatedChecksum = this.generateChecksum(data);
    return calculatedChecksum === checksum;
  }

  /**
   * Protect save data with encryption and checksum
   * Returns: { encrypted: string, checksum: string }
   */
  private protectSaveData(data: any): { encrypted: string; checksum: string } {
    const jsonData = JSON.stringify(data);
    const checksum = this.generateChecksum(jsonData);
    const encrypted = this.encryptData(jsonData);

    return { encrypted, checksum };
  }

  /**
   * Unprotect and validate save data
   * Returns the decrypted data if valid, throws error if tampered
   */
  private unprotectSaveData(encrypted: string, checksum: string): any {
    const decrypted = this.decryptData(encrypted);

    if (!this.validateSaveIntegrity(decrypted, checksum)) {
      throw new Error('Save data integrity check failed - file may be tampered');
    }

    return JSON.parse(decrypted);
  }

  /**
   * Initialize the database and create schema
   */
  public initialize(): void {
    if (this.isInitialized) {
      console.warn('SaveManager: Already initialized');
      return;
    }

    try {
      if (!BetterSqlite3) {
        console.warn("SaveManager: browser mode"); this.isInitialized = false; return;
      }

      // Ensure database directory exists
      const dbDir = dirname(this.dbPath);
      if (!existsSync(dbDir)) {
        console.log('SaveManager: Creating database directory:', dbDir);
        mkdirSync(dbDir, { recursive: true });
      }

      // Open database connection
      this.db = new BetterSqlite3(this.dbPath, { verbose: console.log });
      console.log('SaveManager: Database opened successfully');

      // Enable foreign keys
      this.db.pragma('foreign_keys = ON');

      // Create all tables
      this.createTables();

      // Run migrations to update schema if needed
      this.runMigrations();

      // Verify tables were created
      this.verifySchema();

      this.isInitialized = true;
      console.log('SaveManager: Initialization complete');
    } catch (error) {
      console.error('SaveManager: Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Create all database tables
   */
  private createTables(): void {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    // Create saves table
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS saves (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        slotNumber INTEGER NOT NULL UNIQUE,
        playerName TEXT NOT NULL,
        currentNight INTEGER NOT NULL,
        currentLevel TEXT NOT NULL,
        playerHealth REAL NOT NULL,
        playerStamina REAL NOT NULL,
        playerMaxHealth REAL NOT NULL,
        playerMaxStamina REAL NOT NULL,
        flashlightBattery REAL NOT NULL,
        playerXP INTEGER NOT NULL,
        playerLevel INTEGER NOT NULL,
        playerPosition TEXT NOT NULL,
        lastCheckpoint TEXT,
        playTime INTEGER NOT NULL DEFAULT 0,
        timestamp INTEGER NOT NULL,
        screenshotPath TEXT,
        encryptedData TEXT,
        checksum TEXT,
        CHECK (slotNumber >= 1 AND slotNumber <= 10),
        CHECK (currentNight >= 1 AND currentNight <= 7),
        CHECK (playerHealth >= 0),
        CHECK (playerStamina >= 0),
        CHECK (flashlightBattery >= 0 AND flashlightBattery <= 100)
      );
    `);

    // Create inventory table
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS inventory (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        saveId INTEGER NOT NULL,
        itemId TEXT NOT NULL,
        itemType TEXT NOT NULL,
        quantity INTEGER NOT NULL DEFAULT 1,
        FOREIGN KEY (saveId) REFERENCES saves(id) ON DELETE CASCADE,
        UNIQUE(saveId, itemId),
        CHECK (quantity >= 0)
      );
    `);

    // Create collectibles table
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS collectibles (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        saveId INTEGER NOT NULL,
        collectibleId TEXT NOT NULL,
        collectibleType TEXT NOT NULL CHECK (collectibleType IN ('lore', 'battery', 'health', 'upgrade')),
        collected BOOLEAN NOT NULL DEFAULT 0,
        timestamp INTEGER,
        FOREIGN KEY (saveId) REFERENCES saves(id) ON DELETE CASCADE,
        UNIQUE(saveId, collectibleId)
      );
    `);

    // Create achievements table (global, not per-save)
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS achievements (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        achievementId TEXT NOT NULL UNIQUE,
        unlocked BOOLEAN NOT NULL DEFAULT 0,
        unlockedTimestamp INTEGER,
        progress INTEGER NOT NULL DEFAULT 0,
        CHECK (progress >= 0 AND progress <= 100)
      );
    `);

    // Create settings table (global, not per-save)
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS settings (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        category TEXT NOT NULL,
        settingKey TEXT NOT NULL,
        settingValue TEXT NOT NULL,
        UNIQUE(category, settingKey)
      );
    `);

    // Create stats table (per-save statistics)
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS stats (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        saveId INTEGER NOT NULL,
        statKey TEXT NOT NULL,
        statValue REAL NOT NULL DEFAULT 0,
        FOREIGN KEY (saveId) REFERENCES saves(id) ON DELETE CASCADE,
        UNIQUE(saveId, statKey)
      );
    `);

    console.log('SaveManager: All tables created successfully');
  }

  /**
   * Verify that all tables exist
   */
  private verifySchema(): void {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    const tables = ['saves', 'inventory', 'collectibles', 'achievements', 'settings', 'stats'];
    const existingTables = this.db
      .prepare("SELECT name FROM sqlite_master WHERE type='table'")
      .all() as Array<{ name: string }>;

    const existingTableNames = existingTables.map(t => t.name);

    for (const table of tables) {
      if (!existingTableNames.includes(table)) {
        throw new Error(`SaveManager: Table '${table}' was not created`);
      }
    }

    console.log('SaveManager: Schema verification passed');
    console.log('SaveManager: Existing tables:', existingTableNames);
  }

  /**
   * Run database migrations to update schema
   */
  private runMigrations(): void {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    console.log('SaveManager: Running migrations...');

    // Check if encryptedData and checksum columns exist
    const tableInfo = this.db.prepare('PRAGMA table_info(saves)').all() as Array<{ name: string }>;
    const columnNames = tableInfo.map(col => col.name);

    const hasEncryptedData = columnNames.includes('encryptedData');
    const hasChecksum = columnNames.includes('checksum');

    if (!hasEncryptedData || !hasChecksum) {
      console.log('SaveManager: Missing encryption columns, adding them...');

      try {
        // Add encryptedData column if missing
        if (!hasEncryptedData) {
          this.db.exec('ALTER TABLE saves ADD COLUMN encryptedData TEXT');
          console.log('SaveManager: Added encryptedData column');
        }

        // Add checksum column if missing
        if (!hasChecksum) {
          this.db.exec('ALTER TABLE saves ADD COLUMN checksum TEXT');
          console.log('SaveManager: Added checksum column');
        }

        console.log('SaveManager: Migration complete - encryption columns added');
      } catch (error) {
        console.error('SaveManager: Migration failed:', error);
        throw error;
      }
    } else {
      console.log('SaveManager: All columns present, no migration needed');
    }
  }

  /**
   * Get all save slots
   */
  public getAllSaves(): SaveData[] {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    const saves = this.db.prepare('SELECT * FROM saves ORDER BY slotNumber').all() as SaveData[];
    return saves;
  }

  /**
   * Get save by slot number
   */
  public getSaveBySlot(slotNumber: number): SaveData | null {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    const save = this.db.prepare('SELECT * FROM saves WHERE slotNumber = ?').get(slotNumber) as SaveData | undefined;
    return save || null;
  }

  /**
   * Check if a save slot exists
   */
  public saveSlotExists(slotNumber: number): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    const result = this.db.prepare('SELECT COUNT(*) as count FROM saves WHERE slotNumber = ?').get(slotNumber) as { count: number };
    return result.count > 0;
  }

  /**
   * Create or update a save
   */
  public createSave(saveData: Omit<SaveData, 'id'>): number {
    console.log(`SaveManager.createSave: Starting for slot ${saveData.slotNumber}`);
    console.log(`SaveManager.createSave: Database initialized? ${!!this.db}`);
    console.log(`SaveManager.createSave: IsInitialized flag? ${this.isInitialized}`);

    if (!this.db) {
      console.error('SaveManager.createSave: Database is not initialized!');
      console.warn("SaveManager: no db"); return undefined as any;
    }

    // Check if slot already exists
    console.log(`SaveManager.createSave: Checking if slot ${saveData.slotNumber} exists...`);
    const existing = this.getSaveBySlot(saveData.slotNumber);
    console.log(`SaveManager.createSave: Existing save found? ${!!existing}`);

    try {
      if (existing) {
        // Update existing save
        console.log(`SaveManager.createSave: Updating existing save...`);
        const stmt = this.db.prepare(`
          UPDATE saves SET
            playerName = ?,
            currentNight = ?,
            currentLevel = ?,
            playerHealth = ?,
            playerStamina = ?,
            playerMaxHealth = ?,
            playerMaxStamina = ?,
            flashlightBattery = ?,
            playerXP = ?,
            playerLevel = ?,
            playerPosition = ?,
            lastCheckpoint = ?,
            playTime = ?,
            timestamp = ?,
            screenshotPath = ?,
            encryptedData = ?,
            checksum = ?
          WHERE slotNumber = ?
        `);

        stmt.run(
          saveData.playerName,
          saveData.currentNight,
          saveData.currentLevel,
          saveData.playerHealth,
          saveData.playerStamina,
          saveData.playerMaxHealth,
          saveData.playerMaxStamina,
          saveData.flashlightBattery,
          saveData.playerXP,
          saveData.playerLevel,
          saveData.playerPosition,
          saveData.lastCheckpoint,
          saveData.playTime,
          saveData.timestamp,
          saveData.screenshotPath,
          saveData.encryptedData,
          saveData.checksum,
          saveData.slotNumber
        );

        console.log(`SaveManager: Updated save slot ${saveData.slotNumber}`);
        return existing.id;
      } else {
        // Create new save
        console.log(`SaveManager.createSave: Creating new save...`);
        const stmt = this.db.prepare(`
          INSERT INTO saves (
            slotNumber, playerName, currentNight, currentLevel,
            playerHealth, playerStamina, playerMaxHealth, playerMaxStamina,
            flashlightBattery, playerXP, playerLevel,
            playerPosition, lastCheckpoint, playTime, timestamp, screenshotPath,
            encryptedData, checksum
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        `);

        const result = stmt.run(
          saveData.slotNumber,
          saveData.playerName,
          saveData.currentNight,
          saveData.currentLevel,
          saveData.playerHealth,
          saveData.playerStamina,
          saveData.playerMaxHealth,
          saveData.playerMaxStamina,
          saveData.flashlightBattery,
          saveData.playerXP,
          saveData.playerLevel,
          saveData.playerPosition,
          saveData.lastCheckpoint,
          saveData.playTime,
          saveData.timestamp,
          saveData.screenshotPath,
          saveData.encryptedData,
          saveData.checksum
        );

        console.log(`SaveManager: Created new save in slot ${saveData.slotNumber}`);
        return result.lastInsertRowid as number;
      }
    } catch (error) {
      console.error(`SaveManager.createSave: Database operation failed!`, error);
      console.error(`SaveManager.createSave: Error details:`, (error as Error)?.message);
      console.error(`SaveManager.createSave: Error stack:`, (error as Error)?.stack);
      throw error;
    }
  }

  /**
   * Delete a save by slot number
   */
  public deleteSave(slotNumber: number): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    const stmt = this.db.prepare('DELETE FROM saves WHERE slotNumber = ?');
    const result = stmt.run(slotNumber);

    if (result.changes > 0) {
      console.log(`SaveManager: Deleted save slot ${slotNumber}`);
      return true;
    }

    return false;
  }

  /**
   * Delete ALL saves and reset progress completely
   * This deletes all save slots, inventory, collectibles, stats, and achievements
   */
  public deleteAllSaves(): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      // Delete from all tables (order matters due to foreign keys)
      this.db.exec('DELETE FROM stats');
      this.db.exec('DELETE FROM collectibles');
      this.db.exec('DELETE FROM inventory');
      this.db.exec('DELETE FROM saves');
      this.db.exec('DELETE FROM achievements');
      // Note: We keep settings intact (audio, graphics, controls, etc.)

      console.log('SaveManager: Deleted ALL saves and reset progress');
      return true;
    } catch (error) {
      console.error('SaveManager: Failed to delete all saves:', error);
      return false;
    }
  }

  /**
   * Get database instance (for advanced queries)
   */
  public getDatabase(): Database {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }
    return this.db;
  }

  /**
   * Close database connection
   */
  public close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.isInitialized = false;
      console.log('SaveManager: Database closed');
    }
  }

  /**
   * Save current game state
   */
  public saveGame(
    slotNumber: number,
    playerName: string,
    gameState: {
      currentNight: number;
      currentLevel: string;
      player: {
        health: number;
        stamina: number;
        maxHealth: number;
        maxStamina: number;
        flashlightBattery: number;
        xp: number;
        level: number;
        position: { x: number; y: number };
      };
      lastCheckpoint?: { x: number; y: number } | null;
      playTime: number;
    }
  ): boolean {
    try {
      // Encrypt the full game state for security
      const { encrypted, checksum } = this.protectSaveData(gameState);

      const saveData: Omit<SaveData, 'id'> = {
        slotNumber,
        playerName,
        // Store unencrypted metadata for display purposes
        currentNight: gameState.currentNight,
        currentLevel: gameState.currentLevel,
        playerHealth: gameState.player.health,
        playerStamina: gameState.player.stamina,
        playerMaxHealth: gameState.player.maxHealth,
        playerMaxStamina: gameState.player.maxStamina,
        flashlightBattery: gameState.player.flashlightBattery,
        playerXP: gameState.player.xp,
        playerLevel: gameState.player.level,
        playerPosition: JSON.stringify(gameState.player.position),
        lastCheckpoint: gameState.lastCheckpoint ? JSON.stringify(gameState.lastCheckpoint) : null,
        playTime: gameState.playTime,
        timestamp: Date.now(),
        screenshotPath: null, // TODO: Implement screenshot capture
        // Store encrypted game state with checksum
        encryptedData: encrypted,
        checksum: checksum,
      };

      console.log(`SaveManager: Calling createSave with data for slot ${slotNumber}`);
      this.createSave(saveData);
      console.log(`SaveManager: Game saved to slot ${slotNumber} (encrypted with checksum validation)`);
      return true;
    } catch (error) {
      console.error('SaveManager: Failed to save game - ERROR:', error);
      console.error('SaveManager: Error message:', (error as Error)?.message);
      console.error('SaveManager: Error stack:', (error as Error)?.stack);
      return false;
    }
  }

  /**
   * Load game state from a save slot
   */
  public loadGame(slotNumber: number): {
    currentNight: number;
    currentLevel: string;
    player: {
      health: number;
      stamina: number;
      maxHealth: number;
      maxStamina: number;
      flashlightBattery: number;
      xp: number;
      level: number;
      position: { x: number; y: number };
    };
    lastCheckpoint: { x: number; y: number } | null;
    playTime: number;
    saveInfo: {
      playerName: string;
      timestamp: number;
    };
  } | null {
    try {
      const save = this.getSaveBySlot(slotNumber);

      if (!save) {
        console.error(`SaveManager: No save found in slot ${slotNumber}`);
        return null;
      }

      // If encrypted data exists, decrypt and validate it
      if (save.encryptedData && save.checksum) {
        try {
          const decryptedGameState = this.unprotectSaveData(save.encryptedData, save.checksum);

          // Return the decrypted game state with save info
          const gameState = {
            currentNight: decryptedGameState.currentNight,
            currentLevel: decryptedGameState.currentLevel,
            player: {
              health: decryptedGameState.player.health,
              stamina: decryptedGameState.player.stamina,
              maxHealth: decryptedGameState.player.maxHealth,
              maxStamina: decryptedGameState.player.maxStamina,
              flashlightBattery: decryptedGameState.player.flashlightBattery,
              xp: decryptedGameState.player.xp,
              level: decryptedGameState.player.level,
              position: decryptedGameState.player.position,
            },
            lastCheckpoint: decryptedGameState.lastCheckpoint || null,
            playTime: decryptedGameState.playTime,
            saveInfo: {
              playerName: save.playerName,
              timestamp: save.timestamp,
            },
          };

          console.log(`SaveManager: Game loaded from slot ${slotNumber} (decrypted and validated)`);
          return gameState;
        } catch (error) {
          console.error(`SaveManager: Failed to decrypt save data - file may be corrupted or tampered:`, error);
          throw new Error('Save file integrity check failed. The save file may be corrupted or tampered with.');
        }
      }

      // Fallback: Load from unencrypted fields (for backward compatibility)
      const gameState = {
        currentNight: save.currentNight,
        currentLevel: save.currentLevel,
        player: {
          health: save.playerHealth,
          stamina: save.playerStamina,
          maxHealth: save.playerMaxHealth,
          maxStamina: save.playerMaxStamina,
          flashlightBattery: save.flashlightBattery,
          xp: save.playerXP,
          level: save.playerLevel,
          position: JSON.parse(save.playerPosition),
        },
        lastCheckpoint: save.lastCheckpoint ? JSON.parse(save.lastCheckpoint) : null,
        playTime: save.playTime,
        saveInfo: {
          playerName: save.playerName,
          timestamp: save.timestamp,
        },
      };

      console.log(`SaveManager: Game loaded from slot ${slotNumber} (unencrypted fallback)`);
      return gameState;
    } catch (error) {
      console.error('SaveManager: Failed to load game:', error);
      return null;
    }
  }

  /**
   * Quick save (autosave to a specific slot)
   */
  public quickSave(gameState: Parameters<SaveManager['saveGame']>[2]): boolean {
    const QUICKSAVE_SLOT = 10; // Use slot 10 for quicksaves
    return this.saveGame(QUICKSAVE_SLOT, 'Quicksave', gameState);
  }

  /**
   * Quick load (load from quicksave slot)
   */
  public quickLoad(): ReturnType<SaveManager['loadGame']> {
    const QUICKSAVE_SLOT = 10;
    return this.loadGame(QUICKSAVE_SLOT);
  }

  /**
   * Save a setting
   */
  public saveSetting(category: string, settingKey: string, settingValue: string): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      const stmt = this.db.prepare(`
        INSERT INTO settings (category, settingKey, settingValue)
        VALUES (?, ?, ?)
        ON CONFLICT(category, settingKey) DO UPDATE SET settingValue = ?
      `);

      stmt.run(category, settingKey, settingValue, settingValue);
      console.log(`SaveManager: Saved setting ${category}.${settingKey}`);
      return true;
    } catch (error) {
      console.error('SaveManager: Failed to save setting:', error);
      return false;
    }
  }

  /**
   * Get settings (optionally filtered by category)
   */
  public getSettings(category?: string): SettingsData[] {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      if (category) {
        const settings = this.db
          .prepare('SELECT * FROM settings WHERE category = ?')
          .all(category) as SettingsData[];
        return settings;
      } else {
        const settings = this.db
          .prepare('SELECT * FROM settings')
          .all() as SettingsData[];
        return settings;
      }
    } catch (error) {
      console.error('SaveManager: Failed to get settings:', error);
      return [];
    }
  }

  /**
   * Get a single setting value
   */
  public getSetting(category: string, settingKey: string): string | null {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      const result = this.db
        .prepare('SELECT settingValue FROM settings WHERE category = ? AND settingKey = ?')
        .get(category, settingKey) as { settingValue: string } | undefined;
      return result ? result.settingValue : null;
    } catch (error) {
      console.error('SaveManager: Failed to get setting:', error);
      return null;
    }
  }

  /**
   * Delete a setting
   */
  public deleteSetting(category: string, settingKey: string): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      const stmt = this.db.prepare('DELETE FROM settings WHERE category = ? AND settingKey = ?');
      const result = stmt.run(category, settingKey);
      return result.changes > 0;
    } catch (error) {
      console.error('SaveManager: Failed to delete setting:', error);
      return false;
    }
  }

  /**
   * Save control mapping configuration
   */
  public saveControlMapping(controlMapping: Record<string, string>): boolean {
    try {
      const mappingJson = JSON.stringify(controlMapping);
      return this.saveSetting('controls', 'keyMapping', mappingJson);
    } catch (error) {
      console.error('SaveManager: Failed to save control mapping:', error);
      return false;
    }
  }

  /**
   * Load control mapping configuration
   */
  public loadControlMapping(): Record<string, string> | null {
    try {
      const mappingJson = this.getSetting('controls', 'keyMapping');
      if (!mappingJson) {
        console.log('SaveManager: No control mapping found, using defaults');
        return null;
      }
      return JSON.parse(mappingJson);
    } catch (error) {
      console.error('SaveManager: Failed to load control mapping:', error);
      return null;
    }
  }

  /**
   * Save graphics settings
   */
  public saveGraphicsSettings(settings: {
    resolution?: string;
    fullscreen?: boolean;
    vsync?: boolean;
    quality?: 'low' | 'medium' | 'high' | 'ultra';
    shadows?: boolean;
    particles?: boolean;
    antialiasing?: boolean;
    brightness?: number;
    gamma?: number;
  }): boolean {
    try {
      let allSuccessful = true;

      if (settings.resolution !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'resolution', settings.resolution) && allSuccessful;
      }
      if (settings.fullscreen !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'fullscreen', String(settings.fullscreen)) && allSuccessful;
      }
      if (settings.vsync !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'vsync', String(settings.vsync)) && allSuccessful;
      }
      if (settings.quality !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'quality', settings.quality) && allSuccessful;
      }
      if (settings.shadows !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'shadows', String(settings.shadows)) && allSuccessful;
      }
      if (settings.particles !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'particles', String(settings.particles)) && allSuccessful;
      }
      if (settings.antialiasing !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'antialiasing', String(settings.antialiasing)) && allSuccessful;
      }
      if (settings.brightness !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'brightness', String(settings.brightness)) && allSuccessful;
      }
      if (settings.gamma !== undefined) {
        allSuccessful = this.saveSetting('graphics', 'gamma', String(settings.gamma)) && allSuccessful;
      }

      console.log(`SaveManager: Graphics settings saved ${allSuccessful ? 'successfully' : 'with errors'}`);
      return allSuccessful;
    } catch (error) {
      console.error('SaveManager: Failed to save graphics settings:', error);
      return false;
    }
  }

  /**
   * Load graphics settings
   */
  public loadGraphicsSettings(): {
    resolution: string;
    fullscreen: boolean;
    vsync: boolean;
    quality: 'low' | 'medium' | 'high' | 'ultra';
    shadows: boolean;
    particles: boolean;
    antialiasing: boolean;
    brightness: number;
    gamma: number;
  } {
    try {
      const settings = this.getSettings('graphics');
      const graphicsConfig: any = {
        resolution: '1920x1080',
        fullscreen: false,
        vsync: true,
        quality: 'high',
        shadows: true,
        particles: true,
        antialiasing: true,
        brightness: 1.0,
        gamma: 1.0,
      };

      // Parse settings from database
      for (const setting of settings) {
        switch (setting.settingKey) {
          case 'resolution':
            graphicsConfig.resolution = setting.settingValue;
            break;
          case 'fullscreen':
            graphicsConfig.fullscreen = setting.settingValue === 'true';
            break;
          case 'vsync':
            graphicsConfig.vsync = setting.settingValue === 'true';
            break;
          case 'quality':
            graphicsConfig.quality = setting.settingValue as 'low' | 'medium' | 'high' | 'ultra';
            break;
          case 'shadows':
            graphicsConfig.shadows = setting.settingValue === 'true';
            break;
          case 'particles':
            graphicsConfig.particles = setting.settingValue === 'true';
            break;
          case 'antialiasing':
            graphicsConfig.antialiasing = setting.settingValue === 'true';
            break;
          case 'brightness':
            graphicsConfig.brightness = parseFloat(setting.settingValue);
            break;
          case 'gamma':
            graphicsConfig.gamma = parseFloat(setting.settingValue);
            break;
        }
      }

      console.log('SaveManager: Graphics settings loaded successfully');
      return graphicsConfig;
    } catch (error) {
      console.error('SaveManager: Failed to load graphics settings:', error);
      // Return defaults
      return {
        resolution: '1920x1080',
        fullscreen: false,
        vsync: true,
        quality: 'high',
        shadows: true,
        particles: true,
        antialiasing: true,
        brightness: 1.0,
        gamma: 1.0,
      };
    }
  }

  /**
   * Save audio settings
   */
  public saveAudioSettings(settings: {
    masterVolume?: number;
    musicVolume?: number;
    sfxVolume?: number;
    ambienceVolume?: number;
    voiceVolume?: number;
    muted?: boolean;
    spatialAudio?: boolean;
  }): boolean {
    try {
      let allSuccessful = true;

      if (settings.masterVolume !== undefined) {
        allSuccessful = this.saveSetting('audio', 'masterVolume', String(settings.masterVolume)) && allSuccessful;
      }
      if (settings.musicVolume !== undefined) {
        allSuccessful = this.saveSetting('audio', 'musicVolume', String(settings.musicVolume)) && allSuccessful;
      }
      if (settings.sfxVolume !== undefined) {
        allSuccessful = this.saveSetting('audio', 'sfxVolume', String(settings.sfxVolume)) && allSuccessful;
      }
      if (settings.ambienceVolume !== undefined) {
        allSuccessful = this.saveSetting('audio', 'ambienceVolume', String(settings.ambienceVolume)) && allSuccessful;
      }
      if (settings.voiceVolume !== undefined) {
        allSuccessful = this.saveSetting('audio', 'voiceVolume', String(settings.voiceVolume)) && allSuccessful;
      }
      if (settings.muted !== undefined) {
        allSuccessful = this.saveSetting('audio', 'muted', String(settings.muted)) && allSuccessful;
      }
      if (settings.spatialAudio !== undefined) {
        allSuccessful = this.saveSetting('audio', 'spatialAudio', String(settings.spatialAudio)) && allSuccessful;
      }

      console.log(`SaveManager: Audio settings saved ${allSuccessful ? 'successfully' : 'with errors'}`);
      return allSuccessful;
    } catch (error) {
      console.error('SaveManager: Failed to save audio settings:', error);
      return false;
    }
  }

  /**
   * Load audio settings
   */
  public loadAudioSettings(): {
    masterVolume: number;
    musicVolume: number;
    sfxVolume: number;
    ambienceVolume: number;
    voiceVolume: number;
    muted: boolean;
    spatialAudio: boolean;
  } {
    try {
      const settings = this.getSettings('audio');
      const audioConfig: any = {
        masterVolume: 1.0,
        musicVolume: 0.8,
        sfxVolume: 1.0,
        ambienceVolume: 0.6,
        voiceVolume: 1.0,
        muted: false,
        spatialAudio: true,
      };

      // Parse settings from database
      for (const setting of settings) {
        switch (setting.settingKey) {
          case 'masterVolume':
            audioConfig.masterVolume = parseFloat(setting.settingValue);
            break;
          case 'musicVolume':
            audioConfig.musicVolume = parseFloat(setting.settingValue);
            break;
          case 'sfxVolume':
            audioConfig.sfxVolume = parseFloat(setting.settingValue);
            break;
          case 'ambienceVolume':
            audioConfig.ambienceVolume = parseFloat(setting.settingValue);
            break;
          case 'voiceVolume':
            audioConfig.voiceVolume = parseFloat(setting.settingValue);
            break;
          case 'muted':
            audioConfig.muted = setting.settingValue === 'true';
            break;
          case 'spatialAudio':
            audioConfig.spatialAudio = setting.settingValue === 'true';
            break;
        }
      }

      console.log('SaveManager: Audio settings loaded successfully');
      return audioConfig;
    } catch (error) {
      console.error('SaveManager: Failed to load audio settings:', error);
      // Return defaults
      return {
        masterVolume: 1.0,
        musicVolume: 0.8,
        sfxVolume: 1.0,
        ambienceVolume: 0.6,
        voiceVolume: 1.0,
        muted: false,
        spatialAudio: true,
      };
    }
  }

  /**
   * Export a save to a JSON file
   */
  public exportSave(slotNumber: number, exportPath: string): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      // Get the save data
      const save = this.getSaveBySlot(slotNumber);
      if (!save) {
        console.error(`SaveManager: No save found in slot ${slotNumber}`);
        return false;
      }

      // Get inventory data for this save
      const inventory = this.db
        .prepare('SELECT * FROM inventory WHERE saveId = ?')
        .all(save.id) as InventoryData[];

      // Get collectibles data for this save
      const collectibles = this.db
        .prepare('SELECT * FROM collectibles WHERE saveId = ?')
        .all(save.id) as CollectibleData[];

      // Get stats data for this save
      const stats = this.db
        .prepare('SELECT * FROM stats WHERE saveId = ?')
        .all(save.id) as StatsData[];

      // Create export object
      const exportData = {
        version: '1.0',
        exportDate: Date.now(),
        save,
        inventory,
        collectibles,
        stats,
      };

      // Write to file
      writeFileSync(exportPath, JSON.stringify(exportData, null, 2), 'utf-8');
      console.log(`SaveManager: Exported save slot ${slotNumber} to ${exportPath}`);
      return true;
    } catch (error) {
      console.error('SaveManager: Failed to export save:', error);
      return false;
    }
  }

  /**
   * Import a save from a JSON file
   */
  public importSave(importPath: string, targetSlot?: number): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      // Read the file
      const fileContent = readFileSync(importPath, 'utf-8');
      const importData = JSON.parse(fileContent);

      // Validate import data
      if (!importData.save || !importData.version) {
        console.error('SaveManager: Invalid import file format');
        return false;
      }

      const save = importData.save as SaveData;
      const inventory = importData.inventory as InventoryData[] || [];
      const collectibles = importData.collectibles as CollectibleData[] || [];
      const stats = importData.stats as StatsData[] || [];

      // Use target slot if provided, otherwise use original slot
      const slotNumber = targetSlot || save.slotNumber;

      // Check if slot is valid
      if (slotNumber < 1 || slotNumber > 10) {
        console.error('SaveManager: Invalid slot number');
        return false;
      }

      // Delete existing save in target slot if it exists
      if (this.saveSlotExists(slotNumber)) {
        this.deleteSave(slotNumber);
      }

      // Create the save (without id)
      const saveData: Omit<SaveData, 'id'> = {
        slotNumber,
        playerName: save.playerName,
        currentNight: save.currentNight,
        currentLevel: save.currentLevel,
        playerHealth: save.playerHealth,
        playerStamina: save.playerStamina,
        playerMaxHealth: save.playerMaxHealth,
        playerMaxStamina: save.playerMaxStamina,
        flashlightBattery: save.flashlightBattery,
        playerXP: save.playerXP,
        playerLevel: save.playerLevel,
        playerPosition: save.playerPosition,
        lastCheckpoint: save.lastCheckpoint,
        playTime: save.playTime,
        timestamp: Date.now(), // Update timestamp to current time
        screenshotPath: save.screenshotPath,
      };

      const newSaveId = this.createSave(saveData);

      // Import inventory
      for (const item of inventory) {
        const stmt = this.db.prepare(`
          INSERT INTO inventory (saveId, itemId, itemType, quantity)
          VALUES (?, ?, ?, ?)
        `);
        stmt.run(newSaveId, item.itemId, item.itemType, item.quantity);
      }

      // Import collectibles
      for (const collectible of collectibles) {
        const stmt = this.db.prepare(`
          INSERT INTO collectibles (saveId, collectibleId, collectibleType, collected, timestamp)
          VALUES (?, ?, ?, ?, ?)
        `);
        stmt.run(
          newSaveId,
          collectible.collectibleId,
          collectible.collectibleType,
          collectible.collected,
          collectible.timestamp
        );
      }

      // Import stats
      for (const stat of stats) {
        const stmt = this.db.prepare(`
          INSERT INTO stats (saveId, statKey, statValue)
          VALUES (?, ?, ?)
        `);
        stmt.run(newSaveId, stat.statKey, stat.statValue);
      }

      console.log(`SaveManager: Imported save to slot ${slotNumber} from ${importPath}`);
      return true;
    } catch (error) {
      console.error('SaveManager: Failed to import save:', error);
      return false;
    }
  }

  /**
   * Export all saves to a single JSON file
   */
  public exportAllSaves(exportPath: string): boolean {
    if (!this.db) {
      console.warn("SaveManager: no db"); return undefined as any;
    }

    try {
      // Get all saves
      const saves = this.getAllSaves();

      // Get all related data for each save
      const allData = saves.map(save => {
        const inventory = this.db!
          .prepare('SELECT * FROM inventory WHERE saveId = ?')
          .all(save.id) as InventoryData[];

        const collectibles = this.db!
          .prepare('SELECT * FROM collectibles WHERE saveId = ?')
          .all(save.id) as CollectibleData[];

        const stats = this.db!
          .prepare('SELECT * FROM stats WHERE saveId = ?')
          .all(save.id) as StatsData[];

        return {
          save,
          inventory,
          collectibles,
          stats,
        };
      });

      // Get global achievements
      const achievements = this.db
        .prepare('SELECT * FROM achievements')
        .all() as AchievementData[];

      // Get all settings
      const settings = this.db
        .prepare('SELECT * FROM settings')
        .all() as SettingsData[];

      // Create export object
      const exportData = {
        version: '1.0',
        exportDate: Date.now(),
        saves: allData,
        achievements,
        settings,
      };

      // Write to file
      writeFileSync(exportPath, JSON.stringify(exportData, null, 2), 'utf-8');
      console.log(`SaveManager: Exported all saves to ${exportPath}`);
      return true;
    } catch (error) {
      console.error('SaveManager: Failed to export all saves:', error);
      return false;
    }
  }

  /**
   * Get database info
   */
  public getInfo(): {
    path: string;
    isInitialized: boolean;
    tables: string[];
  } {
    const tables: string[] = [];

    if (this.db) {
      const result = this.db
        .prepare("SELECT name FROM sqlite_master WHERE type='table'")
        .all() as Array<{ name: string }>;
      tables.push(...result.map(t => t.name));
    }

    return {
      path: this.dbPath,
      isInitialized: this.isInitialized,
      tables,
    };
  }
}

// Singleton instance
let saveManagerInstance: SaveManager | null = null;

/**
 * Get the singleton SaveManager instance
 */
export function getSaveManager(): SaveManager {
  if (!saveManagerInstance) {
    saveManagerInstance = new SaveManager();
  }
  return saveManagerInstance;
}
